import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { getRegionMeta } from '../utils/region';
type LiveOrder = {
  material: string;
  quantity: number;
  city: string;
  createdAt: Date;
};
const ORDER_TEMPLATES = [{
  material: '5/8" Minus',
  quantity: 12,
  fallbackCity: 'Tacoma'
}, {
  material: '3-Way Topsoil',
  quantity: 15,
  fallbackCity: 'Kent'
}, {
  material: 'Drain Rock',
  quantity: 10,
  fallbackCity: 'Puyallup'
}, {
  material: 'Medium Bark',
  quantity: 14,
  fallbackCity: 'Bellevue'
}, {
  material: 'Pea Gravel',
  quantity: 8,
  fallbackCity: 'Auburn'
}, {
  material: '5/8" Clear',
  quantity: 11,
  fallbackCity: 'Renton'
}] as const;
type LiveOrderTickerProps = {
  isDispatchActive?: boolean;
};
export function LiveOrderTicker({
  isDispatchActive = true
}: LiveOrderTickerProps) {
  const [now, setNow] = useState(() => new Date());
  const orders = useMemo<LiveOrder[]>(() => {
    const cities = getRegionMeta().cities;
    const created = new Date();
    return ORDER_TEMPLATES.map((order, index) => {
      const createdAt = new Date(created);
      createdAt.setMinutes(createdAt.getMinutes() - (index * 19 + 6));
      return {
        material: order.material,
        quantity: order.quantity,
        city: cities[index % cities.length] || order.fallbackCity,
        createdAt
      };
    });
  }, []);
  useEffect(() => {
    // When dispatch closes, lock the relative time in place so this reads as
    // the last completed feed rather than implying orders are still arriving.
    setNow(new Date());
    if (!isDispatchActive) return;
    const timer = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(timer);
  }, [isDispatchActive]);
  const marqueeOrders = [...orders, ...orders];
  const lastOrder = orders[0];
  if (!isDispatchActive) {
    return <div className="flex min-w-0 flex-1 items-center overflow-hidden" aria-label="Dispatch closed; last delivery activity" data-id="element-1130" data-mp="2oozgv2cmkx13">
        <div className="mr-2 flex shrink-0 items-center gap-1.5" data-id="element-1131" data-mp="z5hnz55ffxq3">
          <span className="h-1.5 w-1.5 rounded-full bg-white/35" aria-hidden="true" data-id="element-1132" data-mp="z7u37ajsvtzg" />
          <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-white/45" data-id="element-1133" data-mp="u7g6uprka8h9">Dispatch closed</span>
        </div>
        <span className="mr-2 shrink-0 text-white/20" aria-hidden="true" data-id="element-1134" data-mp="pdlgx4ld4x9q">•</span>
        <p className="min-w-0 truncate whitespace-nowrap text-[11px] font-medium text-white/35 sm:text-xs" title={`Last delivery activity: ${lastOrder.quantity} yd ${lastOrder.material} to ${lastOrder.city}`} data-id="element-1135" data-mp="kesb7ycbiak4">
          Last delivery: {lastOrder.quantity} yd {lastOrder.material}
          <span className="mx-1.5 text-white/25" data-id="element-1136" data-mp="6hlue9b5mlxj">→</span>
          {lastOrder.city}
        </p>
      </div>;
  }
  return <div className="flex min-w-0 flex-1 items-center overflow-hidden" aria-label="Recent orders" data-id="element-1137" data-mp="1jtv2u86jxjb">
      <div className="mr-2 flex shrink-0 items-center gap-1.5" data-id="element-1138" data-mp="20ubmclhypl2a">
        <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#00b774]" data-id="element-1139" data-mp="1vx36wso77v4q">Live</span>
        <span className="flex h-3 items-end gap-0.5" aria-hidden="true" data-id="element-1140" data-mp="24bzhaoyacxf1">
          {[0, 1, 2].map(index => <motion.span key={index} className="w-0.5 rounded-full bg-[#00b774]" animate={{
          height: ['35%', '100%', '35%']
        }} transition={{
          duration: 1.15,
          repeat: Infinity,
          delay: index * 0.18,
          ease: 'easeInOut'
        }} data-id="element-1141" data-mp="28dkp3vvyqlid" />)}
        </span>
      </div>
      <span className="mr-2 shrink-0 text-white/30" aria-hidden="true" data-id="element-1142" data-mp="1lfhoe47parh7">•</span>
      <div className="min-w-0 overflow-hidden" data-id="element-1143" data-mp="1pf014cpdbw8y">
        <motion.div className="flex w-max items-center whitespace-nowrap" animate={{
        x: ['0%', '-50%']
      }} transition={{
        duration: Math.max(52, orders.length * 9),
        ease: 'linear',
        repeat: Infinity
      }} data-id="element-1144" data-mp="1phulk48dth2d">
          {marqueeOrders.map((order, index) => <span key={`${order.material}-${index}`} className="inline-flex items-center text-[11px] font-medium text-white/50 sm:text-xs" data-id="element-1145" data-mp="35ti4p5d7h2e5" data-mpi={index}>
              <span className="text-white/50" data-id="element-1146" data-mp="nkdq3vmoj2cl">{order.quantity} yd {order.material}</span>
              <span className="mx-1.5 text-[#00b774]" data-id="element-1147" data-mp="ilkb1yrqv3ab">→</span>
              <span data-id="element-1148" data-mp="in575stpiuaa">{order.city}</span>
              <span className="mx-1.5 text-white/35" data-id="element-1149" data-mp="wlbxmnwbtrzx">·</span>
              <span className="text-white/35" data-id="element-1150" data-mp="wmwq87z2z0v5">{formatRelativeTime(order.createdAt, now)}</span>
              <span className="mx-4 text-white/25" data-id="element-1151" data-mp="rkp973ze8uly">•</span>
            </span>)}
        </motion.div>
      </div>
    </div>;
}
function formatRelativeTime(createdAt: Date, now: Date): string {
  const minutes = Math.max(1, Math.round((now.getTime() - createdAt.getTime()) / 60_000));
  if (minutes < 60) return `${minutes} min ago`;
  return `${Math.round(minutes / 60)} hr ago`;
}